import { excelSubtotal, htmlDataRow, moneyCell } from '../features/customers/exportCustomerShared';
import {
  downloadReportExcel,
  printReportPdf,
  reportHeaderRowIndex,
  type ReportMetaLine,
} from '../features/reports/exportReportShared';
import { t } from '../i18n';
import type { ExcelCell } from './exportExcelXml';
import { formatMoney } from './format';
import { escapeHtml } from './printDocument';

export type ListExportFormat = 'excel' | 'pdf';

export async function collectPages<T>(
  load: (page: number) => Promise<{
    data: T[];
    meta: { pageCount: number };
  }>,
): Promise<T[]> {
  const first = await load(1);
  const rows = [...first.data];
  for (let page = 2; page <= first.meta.pageCount; page += 1) {
    const next = await load(page);
    rows.push(...next.data);
  }
  return rows;
}

function addMoney(values: string[]): string {
  let cents = 0;
  for (const value of values) {
    const normalized = String(value).replace(',', '.').trim();
    if (!normalized) continue;
    const negative = normalized.startsWith('-');
    const [whole, fraction = ''] = normalized.replace('-', '').split('.');
    const amount =
      Number(whole || '0') * 100 + Number((fraction + '00').slice(0, 2));
    cents += negative ? -amount : amount;
  }
  const sign = cents < 0 ? '-' : '';
  const absolute = Math.abs(cents);
  return `${sign}${Math.floor(absolute / 100)}.${String(absolute % 100).padStart(2, '0')}`;
}

function pdfTotalsRow(
  columnCount: number,
  numericIndexes: number[],
  amounts: string[],
): string {
  const firstNumeric = numericIndexes[0] ?? columnCount;
  const label =
    firstNumeric > 1
      ? `<td colspan="${firstNumeric}">${escapeHtml(t().common.total)}</td>`
      : `<td>${escapeHtml(t().common.total)}</td>`;
  const money = amounts
    .map((amount) => `<td class="num">${escapeHtml(formatMoney(amount))}</td>`)
    .join('');
  const trailing = columnCount - firstNumeric - numericIndexes.length;
  const empty = Array.from({ length: Math.max(0, trailing) }, () => '<td></td>').join(
    '',
  );
  return `<tr>${label}${money}${empty}</tr>`;
}

export function exportTabularList(input: {
  filename: string;
  title: string;
  meta: ReportMetaLine[];
  headers: string[];
  columnWidths: number[];
  numericIndexes: number[];
  rows: ExcelCell[][];
  pdfRows: string[][];
  moneyTotals?: string[];
  format: ListExportFormat;
}): void {
  const isNumeric = (index: number) => input.numericIndexes.includes(index);
  const headerRow = reportHeaderRowIndex(input.meta.length);
  const firstData = headerRow + 1;
  const lastData = headerRow + input.rows.length;
  const totalExcel: ExcelCell[] | undefined = input.moneyTotals
    ? input.headers.map((_, index) => {
        if (index === 0) {
          return { type: 'string', value: t().common.total, style: 'Label' };
        }
        if (isNumeric(index)) {
          return excelSubtotal(index + 1, firstData, lastData);
        }
        return { type: 'empty' };
      })
    : undefined;

  if (input.format === 'excel') {
    downloadReportExcel({
      filename: input.filename,
      title: input.title,
      meta: input.meta,
      columnCount: input.headers.length,
      columnWidths: input.columnWidths,
      sections: [
        {
          headers: input.headers,
          isNumeric,
          dataRows: input.rows,
          totalRow: totalExcel,
        },
      ],
    });
    return;
  }

  printReportPdf({
    title: input.title,
    meta: input.meta,
    sections: [
      {
        headers: input.headers,
        isNumeric,
        rowHtml: input.pdfRows.map((row) => htmlDataRow(row, isNumeric)),
        totalsRowHtml: input.moneyTotals
          ? pdfTotalsRow(
              input.headers.length,
              input.numericIndexes,
              input.moneyTotals,
            )
          : undefined,
      },
    ],
  });
}

export function textCell(value: string | null | undefined): ExcelCell {
  return { type: 'string', value: value?.trim() ? value : '—' };
}

export function sumMoney(values: Array<string | null | undefined>): string {
  return addMoney(values.map((value) => value ?? '0'));
}

export { moneyCell };
